import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  communityDashboardApiClient,
  CommunityDashboardRequest,
} from '@src/api/tenant/communityDashboard/communityDashboardApiClient';
import { TopActiveChannel } from '@src/api/types/activeChannel';
import { TopActiveParticipant } from '@src/api/types/activeParticipant';
import { DashboardSummary, PeriodRequest } from '@src/api/types/communityDashboard';
import { brandColors } from '@src/assets/colors/brandColors';
import DataTable from '@src/features/tenant/community/component/DataTable';
import SummaryItem from '@src/features/tenant/community/component/SummaryItem';
import ToggleButton from '@src/features/tenant/community/component/ToggleButton';
import { useAsyncHandler } from '@src/hooks/useAsyncHandler';
import { getPresetByKey } from '@src/modules/constants/dateRangePreset';
import { TENANT_URL } from '@src/routes/tenant';
import formatDate from '@src/utils/formatDate';
import { subDays } from 'date-fns';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';

import SummaryDateRangeSelector from './component/SummaryDateRangeSelector/SummaryDateRangeSelector';
import SummaryGraphChartjs from './component/SummaryGraphChartjs';

const toggleOptions = [
  { id: 'user', label: 'ユーザー数', width: '82px' },
  { id: 'company', label: '社数', width: '44px' },
];

const participantColumns = [
  { title: '', key: 'rank', width: 'w-[42px]' },
  { title: '企業名', key: 'company_name', width: 'w-[160px]' },
  { title: '氏名', key: 'full_name', width: 'w-[120px]' },
  { title: '参加チャンネル数', key: 'channels_count', width: 'w-[100px]', nowrap: true },
  { title: '投稿数', key: 'posts', width: 'w-[80px]' },
  { title: 'リアクション数', key: 'reactions', width: 'w-[100px]' },
  { title: '総アクション数', key: 'total_actions', width: 'w-[100px]' },
  { title: '最終アクセス日', key: 'last_accessed_at', width: 'w-[120px]', nowrap: true },
];

const channelColumns = [
  { title: '', key: 'rank', width: 'w-[42px]' },
  { title: 'チャンネル名', key: 'name', width: 'w-[180px]' },
  { title: '公開設定', key: 'visibility', width: 'w-[80px]' },
  { title: '作成日', key: 'created_at', width: 'w-[120px]', nowrap: true },
  { title: '参加人数', key: 'members_count', width: 'w-[80px]' },
  { title: '投稿数', key: 'posts', width: 'w-[80px]' },
  { title: 'リアクション数', key: 'reactions', width: 'w-[100px]' },
  { title: '総アクション数', key: 'total_actions', width: 'w-[100px]' },
  { title: '最新の投稿日', key: 'latest_posted_at', width: 'w-[120px]', nowrap: true },
];

type SummaryTabKey =
  | 'user_count'
  | 'access'
  | 'reaction_users'
  | 'posting_users'
  | 'total_reactions'
  | 'total_posts';
const DEFAULT_PRESET = '7' as const;

type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
  preset: PeriodRequest | null;
};

type CommunityDashboardData = {
  summary: DashboardSummary;
  top_participants: TopActiveParticipant[];
  top_channels: TopActiveChannel[];
};

const CommunityDashboard = () => {
  const today = dayjs().toDate();

  const [isSummaryCountUserSelected, setIsSummaryCountUserSelected] = useState<boolean>(true);
  const [selectedSummaryTab, setSelectedSummaryTab] = useState<SummaryTabKey>('user_count');
  const [isDateRangeOpen, setIsDateRangeOpen] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: subDays(today, 7),
    endDate: subDays(today, 1),
    preset: null,
  });
  const [availableDateRange, setAvailableDateRange] = useState<{
    earliest: Date | null;
    latest: Date | null;
  }>({ earliest: null, latest: null });

  const [communityDashboardData, setCommunityDashboardData] = useState<CommunityDashboardData>(
    {} as CommunityDashboardData,
  );
  const [graphLabels, setGraphLabels] = useState<string[]>([]);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  const { loading: loadingFetchCommunityDashboardData, execute: fetchCommunityDashboardData } =
    useAsyncHandler(async (range?: DateRange) => {
      const params: CommunityDashboardRequest = {
        start_date: formatDate(range?.startDate || dateRange.startDate, 'YYYY-MM-DD'),
        end_date: formatDate(range?.endDate || dateRange.endDate, 'YYYY-MM-DD'),
        period: range?.preset || dateRange.preset || DEFAULT_PRESET,
        view_by: isSummaryCountUserSelected ? 'users' : 'companies',
      };

      const response = await communityDashboardApiClient.index(params);
      handleApiResponse(response, (data) => {
        const validation = data.validation_messages ?? [];

        if (validation.length > 0) {
          setValidationMessages(validation);
          return;
        }

        setCommunityDashboardData({
          summary: data.summary,
          top_participants: data.top_participants,
          top_channels: data.top_channels,
        });
        setGraphLabels(data.graph_labels);
        setAvailableDateRange({
          earliest: data.available_date_range?.earliest
            ? dayjs(data.available_date_range.earliest).toDate()
            : null,
          latest: data.available_date_range?.latest
            ? dayjs(data.available_date_range.latest).toDate()
            : null,
        });

        const { type, start_date, end_date } = data.period || {};
        const isAll = type === 'all';
        const shouldOverwriteDate = isAll;

        if (shouldOverwriteDate) {
          setDateRange({
            preset: type as PeriodRequest,
            startDate: start_date ? dayjs(start_date).toDate() : null,
            endDate: end_date ? dayjs(end_date).toDate() : null,
          });
        }

        setIsDateRangeOpen(false);
      });
    });

  const handleDateRangeChange = async (range: {
    startDate: Date | null;
    endDate: Date | null;
    preset: PeriodRequest | null;
  }) => {
    if (!(range?.startDate && range?.endDate) && !range?.preset) {
      return;
    }

    setDateRange(range);
    await fetchCommunityDashboardData(range);
  };

  const handleToggleChange = (id: string) => {
    const newIsUserSelected = id === 'user';
    setIsSummaryCountUserSelected(newIsUserSelected);

    const params: CommunityDashboardRequest = {
      start_date: formatDate(dateRange.startDate, 'YYYY-MM-DD'),
      end_date: formatDate(dateRange.endDate, 'YYYY-MM-DD'),
      period: dateRange.preset || DEFAULT_PRESET,
      view_by: newIsUserSelected ? 'users' : 'companies',
    };

    communityDashboardApiClient.index(params).then((response) => {
      handleApiResponse(response, (data) => {
        if (data.validation_messages?.length) {
          setValidationMessages(data.validation_messages);
          return;
        }

        setCommunityDashboardData({
          summary: data.summary,
          top_participants: data.top_participants,
          top_channels: data.top_channels,
        });
        setGraphLabels(data.graph_labels);
        setValidationMessages([]);
      });
    });
  };

  useEffect(() => {
    fetchCommunityDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedDateRangeText = useMemo(() => {
    if (dateRange.startDate && dateRange.endDate) {
      return `${dayjs(dateRange.startDate).format('YYYY年MM月DD日')} - ${dayjs(dateRange.endDate).format('YYYY年MM月DD日')}`;
    }
    return '全期間（最古〜昨日）';
  }, [dateRange.startDate, dateRange.endDate]);

  const buttonLabel = useMemo(() => {
    return getPresetByKey(dateRange.preset).label;
  }, [dateRange.preset]);

  const loading = loadingFetchCommunityDashboardData;

  return (
    <div className='bg-[#F9F9F9] px-[clamp(5.77%,60px,60px)] pb-[clamp(3.07%,32px,32px)] pt-[68px] lg:pt-[100px]'>
      <div className='bg-white p-6'>
        <div>
          <div className='flex items-center justify-between'>
            <h1 className='text-[24px] font-semibold leading-loose'>サマリー</h1>
            <div className='flex items-center gap-3'>
              <div className='text-xs'>{formattedDateRangeText}</div>
              <div className='relative'>
                <button
                  onClick={() => {
                    setIsDateRangeOpen((prev) => !prev);
                  }}
                  className='w-[190px] appearance-none rounded-[4px] border border-solid border-gray-250 bg-down bg-[center_right_12px] bg-no-repeat py-[10px] pl-4 pr-7 text-left text-sm font-medium'
                >
                  {buttonLabel}
                </button>
                {isDateRangeOpen && (
                  <div className={`absolute right-0 top-[50px] z-[9999]`}>
                    <SummaryDateRangeSelector
                      onChange={handleDateRangeChange}
                      onClose={() => setIsDateRangeOpen(false)}
                      initialStartDate={dateRange.startDate}
                      initialEndDate={dateRange.endDate}
                      initialPreset={dateRange.preset || DEFAULT_PRESET}
                      availableDateRange={availableDateRange}
                      errorMessages={validationMessages}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='mt-2 flex rounded-[4px] border p-3'>
            <div className='w-[71%]'>
              <div className='flex items-center justify-between'>
                <h2 className='text-sm font-semibold'>利用状況</h2>
                <div className='flex items-center bg-[#efefef]'></div>
                <ToggleButton
                  options={toggleOptions}
                  selectedId={isSummaryCountUserSelected ? 'user' : 'company'}
                  onChange={(id) => handleToggleChange(id)}
                />
              </div>
              <ul className='mt-2 flex flex-wrap gap-2'>
                {communityDashboardData.summary &&
                  Object.entries(communityDashboardData?.summary)
                    .filter(([key]) =>
                      ['user_count', 'access', 'reaction_users', 'posting_users'].includes(key),
                    )
                    .map(([key, data]) => (
                      <SummaryItem
                        key={key}
                        title={data.title}
                        value={data.value}
                        unit={data.unit}
                        isSelected={selectedSummaryTab === (key as SummaryTabKey)}
                        onClick={() => setSelectedSummaryTab(key as SummaryTabKey)}
                      />
                    ))}
              </ul>
            </div>
            <div className='flex w-[29%]'>
              <div className='mx-3 h-full w-px bg-[#D9D9D9]'></div>
              <div className='size-full'>
                <h2 className='text-sm font-semibold'>アクション状況</h2>
                <ul className='mt-2 flex flex-wrap gap-2'>
                  {communityDashboardData.summary &&
                    Object.entries(communityDashboardData?.summary)
                      .filter(([key]) => ['total_reactions', 'total_posts'].includes(key))
                      .map(([key, data]) => (
                        <li
                          key={key}
                          className='w-[calc(50%-4px)]'
                        >
                          <a
                            className={`flex w-full flex-1 cursor-pointer flex-col gap-1 border-b-2 px-[calc((12/144)*100%)] pb-1 pt-2 
                              ${
                                selectedSummaryTab === (key as SummaryTabKey)
                                  ? 'border-b-primary-500 bg-[#F3F7FD] text-primary-500'
                                  : 'border-transparent text-[#666666]'
                              }`}
                            onClick={() => setSelectedSummaryTab(key as SummaryTabKey)}
                          >
                            <h3 className='text-xs font-medium leading-none'>{data.title}</h3>
                            <div
                              className={`break-words text-lg font-medium ${selectedSummaryTab !== (key as SummaryTabKey) ? 'text-black' : ''}`}
                            >
                              {data.value}
                              <span className='text-xs font-medium'>件</span>
                            </div>
                          </a>
                        </li>
                      ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className='relative mt-6'>
          {communityDashboardData.summary &&
            selectedSummaryTab &&
            communityDashboardData.summary[selectedSummaryTab] && (
              <React.Fragment>
                <SummaryGraphChartjs
                  graphLabels={graphLabels}
                  graphData={communityDashboardData.summary[selectedSummaryTab].graph_data.map(
                    (item) => ({
                      date: item.date,
                      score: item.count,
                    }),
                  )}
                  label={communityDashboardData.summary[selectedSummaryTab].title}
                />
                {loading && (
                  <div className='absolute inset-0 flex items-center justify-center bg-black/5'>
                    <ClipLoader
                      size={50}
                      color={brandColors['500']}
                      loading={loading}
                    />
                  </div>
                )}
              </React.Fragment>
            )}
        </div>
      </div>

      <DataTable
        title='活発な参加者'
        columns={participantColumns}
        data={communityDashboardData?.top_participants}
        dateRange={dateRange}
        seeMoreLink={TENANT_URL.COMMUNITY_LIST_PARTICIPANT}
      />

      <DataTable
        title='活発なチャンネル'
        columns={channelColumns}
        data={communityDashboardData?.top_channels}
        dateRange={dateRange}
        seeMoreLink={TENANT_URL.COMMUNITY_CHANNEL_ACTIVITY}
      />
    </div>
  );
};

export default CommunityDashboard;
